import React, { useEffect, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FilterOperations as FO } from 'business/definitions';
import {
    dynamicYearFilterValue,
    isDynamicYearFilter,
    nestedYearFilterOperations,
} from './helpers/definitions';
import { useRenderOperationValue } from './hooks/valueRenderers';
import NestedFilterOperationSelector from './OperationSelectorItem/NestedFilterOperationSelector';
import SimpleFilterOperationSelector from './OperationSelectorItem/SimpleFilterOperationSelector';

const DEFAULT_YEAR_FILTER: Partial<SentenceFilterT> = {
    operation: FO.equal,
    value: dynamicYearFilterValue.current,
};

type Props = {
    filter: SentenceFilterT;
    isDisabled: boolean;
    setFilter: (fields: Partial<SentenceFilterT>) => void;
    isPerimeterFilter: boolean;
};

const YearFilterOperationSelector = ({
    filter,
    isDisabled,
    setFilter,
    isPerimeterFilter,
}: Props) => {
    const { value, operation } = filter;

    useEffect(() => {
        const isFilterOperationNull = operation === FO.is_null;
        if (!isPerimeterFilter && !isFilterOperationNull && !value) {
            setFilter(DEFAULT_YEAR_FILTER);
        }
    }, []);

    const isDynamicFilter = value && isDynamicYearFilter(value);
    const menuValue = isDynamicFilter ? `${operation}-${value}` : operation;
    const renderValue = useRenderOperationValue(
        filter,
        !!isDynamicFilter,
        isPerimeterFilter,
    );
    const [open, setOpen] = useState(false);

    const handleChangeFilter = (fields: Partial<SentenceFilterT>) => {
        setFilter(fields);
        setOpen(false);
    };

    return (
        <FormControl variant="standard" fullWidth={false}>
            <Select
                variant="standard"
                key={`${operation}-${value}`}
                disabled={isDisabled}
                value={menuValue}
                renderValue={renderValue}
                IconComponent={KeyboardArrowDownIcon}
                disableUnderline
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
            >
                {nestedYearFilterOperations.map((menuOperation) => (
                    <NestedFilterOperationSelector
                        key={menuOperation}
                        menuOperation={menuOperation}
                        onClose={() => setOpen(false)}
                        setFilter={handleChangeFilter}
                    />
                ))}
                {!isPerimeterFilter && ( // Perimeter filters cannot be null
                    <SimpleFilterOperationSelector
                        menuOperation={FO.is_null}
                        setFilter={handleChangeFilter}
                    />
                )}
            </Select>
        </FormControl>
    );
};

export default React.memo(YearFilterOperationSelector);
